import React, { useRef, useEffect, useState } from "react";
import EmailEditor from "react-email-editor";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  IconButton,
  ThemeProvider,
  Chip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const DragAndDropEmailEditor = ({
  open,
  onClose,
  onSubmit,
  template,
  theme,
}) => {
  const { t } = useTranslation();
  const emailEditorRef = useRef(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    if (template) {
      setName(template.name || "");
      setSubject(template.subject || "");
    } else {
      setName("");
      setSubject("");
    }
  }, [template, open]);

  // Load template design if available
  const onEditorReady = () => {
    // Hide the loading indicator once the editor is ready
    setInitializing(false);

    // If editing an existing template with Unlayer design data
    if (template && template.design) {
      try {
        emailEditorRef.current.editor.loadDesign(JSON.parse(template.design));
      } catch (err) {
        console.error("Error loading template design:", err);
        // If the design isn't valid JSON or there's another issue, continue without loading
      }
    }
  };

  const handleSave = () => {
    setLoading(true);
    setError("");

    if (!name.trim()) {
      setError(t("dragDropEditor.templateNameRequired"));
      setLoading(false);
      return;
    }

    if (!subject.trim()) {
      setError(t("dragDropEditor.subjectRequired"));
      setLoading(false);
      return;
    }

    // Get both the design JSON and HTML
    emailEditorRef.current.editor.saveDesign((design) => {
      emailEditorRef.current.editor.exportHtml((data) => {
        const { html } = data;

        // Extract body content using DOMParser
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        const bodyContent = doc.body.innerHTML;

        const templateData = {
          name: name,
          subject: subject,
          body: bodyContent, // Store only body content
          design: JSON.stringify(design), // Now design is properly defined
        };

        onSubmit(templateData);
        setLoading(false);
      });
    });
  };

  // Extract placeholders from HTML content
  const extractPlaceholders = (htmlContent) => {
    const placeholderRegex = /{{([^{}]+)}}/g;
    const matches = [...htmlContent.matchAll(placeholderRegex)] || [];
    const placeholders = new Set();

    matches.forEach((match) => placeholders.add(match[1]));
    return [...placeholders];
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0px 8px 32px rgba(0, 23, 231, 0.15)",
            height: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2.5,
            px: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {template
              ? t("dragDropEditor.editEmailTemplate")
              : t("dragDropEditor.createNewEmailTemplate")}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            sx={{
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{ p: 0, display: "flex", flexDirection: "column" }}
        >
          <Box sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
            <Box display="flex" gap={2}>
              <TextField
                label={t("dragDropEditor.templateName")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                margin="dense"
                variant="outlined"
                error={error === t("dragDropEditor.templateNameRequired")}
                helperText={
                  error === t("dragDropEditor.templateNameRequired")
                    ? error
                    : ""
                }
                sx={{ backgroundColor: "white" }}
              />
              <TextField
                label={t("dragDropEditor.emailSubject")}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                fullWidth
                margin="dense"
                variant="outlined"
                error={error === t("dragDropEditor.subjectRequired")}
                helperText={
                  error === t("dragDropEditor.subjectRequired") ? error : ""
                }
                sx={{ backgroundColor: "white" }}
              />
            </Box>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 1, display: "block" }}
            >
              {t("dragDropEditor.placeholderInstructions")}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1, position: "relative" }}>
            {initializing && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255,255,255,0.7)",
                  zIndex: 10,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <EmailEditor
              ref={emailEditorRef}
              onReady={onEditorReady}
              options={{
                displayMode: "email",
                features: {
                  imageEditor: {
                    tools: ["crop", "resize", "filters"],
                  },
                },
                tools: {
                  html: {
                    enabled: false,
                  },
                  image: {
                    enabled: true,
                  },
                },
                mergeTags: [
                  {
                    name: t("dragDropEditor.candidate"),
                    items: [
                      { value: "{{first_name}}", name: t("dragDropEditor.firstName") },
                      { value: "{{last_name}}", name: t("dragDropEditor.lastName") },
                      { value: "{{company}}", name: t("dragDropEditor.company") },
                      { value: "{{position}}", name: t("dragDropEditor.position") },
                    ],
                  },
                  {
                    name: t("dragDropEditor.interviewDetails"),
                    items: [
                      { value: "{{date}}", name: t("dragDropEditor.date") },
                      { value: "{{time}}", name: t("dragDropEditor.time") },
                      { value: "{{location}}", name: t("dragDropEditor.location") },
                    ],
                  },
                ],
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, justifyContent: "space-between", gap: 2 }}>
          <Button 
            onClick={onClose} 
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              borderColor: "#0017E7",
              color: "#0017E7",
              fontWeight: 500,
              "&:hover": {
                borderColor: "#0010a3",
                backgroundColor: "rgba(0, 23, 231, 0.04)",
              },
            }}
          >
            {t("dragDropEditor.cancel")}
          </Button>
          <Box>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} sx={{ color: "white" }} />}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.2,
                fontWeight: 600,
                background: "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
                boxShadow: "0px 4px 12px rgba(0, 23, 231, 0.3)",
                "&:hover": {
                  background: "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
                  boxShadow: "0px 6px 16px rgba(0, 23, 231, 0.4)",
                  transform: "translateY(-1px)",
                },
                "&:disabled": {
                  background: "#ccc",
                  color: "#666",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              {template
                ? t("dragDropEditor.updateTemplate")
                : t("dragDropEditor.saveTemplate")}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default DragAndDropEmailEditor;
